/**
 * Trust Profile Test Component
 * Epic 7: UI Testing Component for Trust, Reputation & Marketplace Governance
 */

import React, { useState } from 'react';
import {
  Box,
  Card,
  CardContent,
  TextField,
  Button,
  Typography,
  Alert,
  CircularProgress,
  Chip,
  Grid,
  Divider,
  Paper,
  Accordion,
  AccordionSummary,
  AccordionDetails,
} from '@mui/material';
import {
  ExpandMore,
  Security,
  TrendingUp,
  Warning,
  CheckCircle,
} from '@mui/icons-material';
import api from '../../../shared/api';

const TrustProfileTest = () => {
  const [providerId, setProviderId] = useState('');
  const [customerId, setCustomerId] = useState('');
  const [providerData, setProviderData] = useState(null);
  const [customerData, setCustomerData] = useState(null);
  const [governanceData, setGovernanceData] = useState(null);
  const [providerHistory, setProviderHistory] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const fetchProviderTrust = async () => {
    if (!providerId) return;
    setLoading(true);
    setError(null);
    try {
      const result = await api.get(`/intelligence/trust/providers/${providerId}`);
      if (result.success) {
        setProviderData(result.data);
      } else {
        setError(result.error || 'Failed to fetch provider trust profile');
      }
    } catch (err) {
      setError(err.message || 'Failed to fetch provider trust profile');
    } finally {
      setLoading(false);
    }
  };

  const fetchCustomerTrust = async () => {
    if (!customerId) return;
    setLoading(true);
    setError(null);
    try {
      const result = await api.get(`/intelligence/trust/customers/${customerId}`);
      if (result.success) {
        setCustomerData(result.data);
      } else {
        setError(result.error || 'Failed to fetch customer trust profile');
      }
    } catch (err) {
      setError(err.message || 'Failed to fetch customer trust profile');
    } finally {
      setLoading(false);
    }
  };

  const fetchGovernanceAlerts = async () => {
    setLoading(true);
    setError(null);
    try {
      const result = await api.get('/intelligence/governance/alerts');
      if (result.success) {
        setGovernanceData(result.data);
      } else {
        setError(result.error || 'Failed to fetch governance alerts');
      }
    } catch (err) {
      setError(err.message || 'Failed to fetch governance alerts');
    } finally {
      setLoading(false);
    }
  };

  const fetchProviderHistory = async () => {
    if (!providerId) return;
    setLoading(true);
    setError(null);
    try {
      const result = await api.get(`/intelligence/trust/providers/${providerId}/history?limit=10`);
      if (result.success) {
        setProviderHistory(result.data);
      } else {
        setError(result.error || 'Failed to fetch trust history');
      }
    } catch (err) {
      setError(err.message || 'Failed to fetch trust history');
    } finally {
      setLoading(false);
    }
  };

  const getTierColor = (tier) => {
    switch (tier) {
      case 'PREFERRED': return 'success';
      case 'VERIFIED': return 'info';
      case 'PROVISIONAL': return 'warning';
      case 'RESTRICTED': return 'error';
      default: return 'default';
    }
  };

  const getRiskColor = (risk) => {
    switch (risk) {
      case 'HIGH': return 'error';
      case 'MEDIUM': return 'warning';
      case 'LOW': return 'success';
      default: return 'default';
    }
  };

  return (
    <Box p={3}>
      <Typography variant="h4" gutterBottom sx={{ mb: 3 }}>
        <Security sx={{ mr: 1, verticalAlign: 'middle' }} />
        Epic 7: Trust Profile Testing
      </Typography>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      <Grid container spacing={3}>
        {/* Provider Trust */}
        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Provider Trust Profile
              </Typography>
              <Box display="flex" gap={2} mb={2}>
                <TextField
                  label="Provider ID"
                  value={providerId}
                  onChange={(e) => setProviderId(e.target.value)}
                  size="small"
                  fullWidth
                  placeholder="e.g., PROV-123"
                />
                <Button
                  variant="contained"
                  onClick={fetchProviderTrust}
                  disabled={loading || !providerId}
                  startIcon={loading ? <CircularProgress size={16} /> : <Security />}
                >
                  Fetch
                </Button>
              </Box>

              {providerData && (
                <Box>
                  <Divider sx={{ my: 2 }} />
                  <Grid container spacing={2}>
                    <Grid item xs={12}>
                      <Typography variant="body2" color="text.secondary">
                        Trust Score
                      </Typography>
                      <Box display="flex" alignItems="center" gap={2} mt={1}>
                        <Typography variant="h3">
                          {providerData.trustScore}
                        </Typography>
                        <Typography variant="h6" color="text.secondary">
                          / 100
                        </Typography>
                      </Box>
                    </Grid>
                    <Grid item xs={12}>
                      <Typography variant="body2" color="text.secondary">
                        Trust Tier
                      </Typography>
                      <Chip
                        label={providerData.trustTier}
                        color={getTierColor(providerData.trustTier)}
                        sx={{ mt: 1 }}
                        icon={<Security />}
                      />
                    </Grid>
                    <Grid item xs={12}>
                      <Typography variant="body2" color="text.secondary" gutterBottom>
                        Explanation
                      </Typography>
                      <Paper variant="outlined" sx={{ p: 2, mt: 1 }}>
                        <Typography variant="body2">
                          {providerData.explanation || 'No explanation available'}
                        </Typography>
                      </Paper>
                    </Grid>
                    <Grid item xs={12}>
                      <Accordion>
                        <AccordionSummary expandIcon={<ExpandMore />}>
                          <Typography variant="subtitle2">Signals Breakdown</Typography>
                        </AccordionSummary>
                        <AccordionDetails>
                          <Box component="pre" sx={{ fontSize: '0.75rem', overflow: 'auto' }}>
                            {JSON.stringify(providerData.signals, null, 2)}
                          </Box>
                        </AccordionDetails>
                      </Accordion>
                    </Grid>
                    <Grid item xs={12}>
                      <Button
                        variant="outlined"
                        size="small"
                        onClick={fetchProviderHistory}
                        disabled={loading || !providerId}
                      >
                        View History
                      </Button>
                    </Grid>
                  </Grid>
                </Box>
              )}
            </CardContent>
          </Card>
        </Grid>

        {/* Customer Trust */}
        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Customer Trust Profile
              </Typography>
              <Box display="flex" gap={2} mb={2}>
                <TextField
                  label="Customer ID"
                  value={customerId}
                  onChange={(e) => setCustomerId(e.target.value)}
                  size="small"
                  fullWidth
                  placeholder="e.g., customer-123"
                />
                <Button
                  variant="contained"
                  onClick={fetchCustomerTrust}
                  disabled={loading || !customerId}
                  startIcon={loading ? <CircularProgress size={16} /> : <Security />}
                >
                  Fetch
                </Button>
              </Box>

              {customerData && (
                <Box>
                  <Divider sx={{ my: 2 }} />
                  <Grid container spacing={2}>
                    <Grid item xs={12}>
                      <Typography variant="body2" color="text.secondary">
                        Risk Flag
                      </Typography>
                      <Chip
                        label={customerData.customerRiskFlag || 'N/A'}
                        color={getRiskColor(customerData.customerRiskFlag)}
                        sx={{ mt: 1 }}
                        icon={
                          customerData.customerRiskFlag === 'HIGH' ? (
                            <Warning />
                          ) : customerData.customerRiskFlag === 'MEDIUM' ? (
                            <Warning />
                          ) : (
                            <CheckCircle />
                          )
                        }
                      />
                    </Grid>
                    <Grid item xs={12}>
                      <Alert severity="info" sx={{ mt: 1 }}>
                        <Typography variant="caption">
                          <strong>Note:</strong> Customer trust signals are ops-only risk indicators.
                          They never influence pricing, eligibility, or routing exclusion.
                        </Typography>
                      </Alert>
                    </Grid>
                    <Grid item xs={12}>
                      <Typography variant="body2" color="text.secondary" gutterBottom>
                        Explanation
                      </Typography>
                      <Paper variant="outlined" sx={{ p: 2, mt: 1 }}>
                        <Typography variant="body2">
                          {customerData.explanation || 'No explanation available'}
                        </Typography>
                      </Paper>
                    </Grid>
                    <Grid item xs={12}>
                      <Accordion>
                        <AccordionSummary expandIcon={<ExpandMore />}>
                          <Typography variant="subtitle2">Signals Breakdown</Typography>
                        </AccordionSummary>
                        <AccordionDetails>
                          <Box component="pre" sx={{ fontSize: '0.75rem', overflow: 'auto' }}>
                            {JSON.stringify(customerData.signals, null, 2)}
                          </Box>
                        </AccordionDetails>
                      </Accordion>
                    </Grid>
                  </Grid>
                </Box>
              )}
            </CardContent>
          </Card>
        </Grid>

        {/* Governance Alerts */}
        <Grid item xs={12}>
          <Card>
            <CardContent>
              <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                <Typography variant="h6">
                  <TrendingUp sx={{ mr: 1, verticalAlign: 'middle' }} />
                  Marketplace Governance Alerts
                </Typography>
                <Button
                  variant="outlined"
                  onClick={fetchGovernanceAlerts}
                  disabled={loading}
                  startIcon={loading ? <CircularProgress size={16} /> : <TrendingUp />}
                >
                  Fetch All Alerts
                </Button>
              </Box>

              {governanceData && (
                <Box>
                  <Typography variant="body2" color="text.secondary" gutterBottom>
                    Total Alerts: {governanceData.alerts?.length || 0}
                  </Typography>
                  {governanceData.alerts && governanceData.alerts.length > 0 ? (
                    governanceData.alerts.map((alert, index) => (
                      <Alert
                        key={index}
                        severity={
                          alert.severity === 'HIGH' ? 'error' :
                          alert.severity === 'MEDIUM' ? 'warning' : 'info'
                        }
                        sx={{ mb: 1 }}
                        icon={<Warning />}
                      >
                        <Typography variant="subtitle2">{alert.type || alert.alertType}</Typography>
                        <Typography variant="body2" sx={{ mt: 0.5 }}>
                          {alert.message}
                        </Typography>
                        {alert.recommendation && (
                          <Typography variant="caption" display="block" sx={{ mt: 1 }}>
                            <strong>Recommendation:</strong> {alert.recommendation}
                          </Typography>
                        )}
                        {alert.suggestedAction && (
                          <Typography variant="caption" display="block" sx={{ mt: 0.5 }}>
                            <strong>Suggested Action:</strong> {alert.suggestedAction}
                          </Typography>
                        )}
                      </Alert>
                    ))
                  ) : (
                    <Alert severity="success">
                      No governance alerts at this time. Marketplace is healthy.
                    </Alert>
                  )}
                </Box>
              )}
            </CardContent>
          </Card>
        </Grid>

        {/* Provider History */}
        {providerHistory && providerHistory.length > 0 && (
          <Grid item xs={12}>
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  Trust Profile History
                </Typography>
                <Box component="pre" sx={{ fontSize: '0.75rem', overflow: 'auto', p: 2, bgcolor: 'grey.50' }}>
                  {JSON.stringify(providerHistory, null, 2)}
                </Box>
              </CardContent>
            </Card>
          </Grid>
        )}
      </Grid>
    </Box>
  );
};

export default TrustProfileTest;
